import {
  CardIndicators,
  LastRegistrationApp,
  MetricSummaryWidgets,
  NavFilters,
  OffcanvasFilters,
  OrganizationOnlineUser,
  PageWrapper,
  WidgetsActivities,
} from '@repo/ui';
import { GetAllCoursesUserUseCaseInterface } from 'domain/services/courses/get-all-courses-user';
import {
  GetAllGroupsUserUseCaseInterface,
  GetAllOrganizationsUserUseCaseInterface,
  GetAllSemestersUserUseCaseInterface,
} from 'domain/usecases';
import { GetAllCurricularUnitsUserUseCaseInterface } from 'domain/usecases/curricular-units';
import { GetAllUsersMetrics } from 'domain/usecases/dashboard';
import { useFormik } from 'formik';
import { ChartOngoingActivityByInstitution } from 'presentation/app/components/charts/chart-ongoing-activities-by-institution';
import { ChartUsersOnlinePerPlatform } from 'presentation/app/components/charts/chart-users-online-per-platform';
import { useAuth } from 'presentation/app/hooks/useAuth';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { clearDashboardSummary } from 'presentation/config/store/dashboard/dashboard-get-summary.slice';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import * as Yup from 'yup';

import { makeColumnsOnlineUsersDashboardIndicators } from '../common/columns/columns-online-users-dashboard-indicators';
import { DashboardMaps } from '../components/dashboard-maps';
import { WidgetsCH } from '../components/widgets-ch';
import { useDashboard } from '../hooks/useDashboard';
import { IFiltersFormatted, useFilters } from '../hooks/useFilters';

import './styles.css';

interface IProps {
  getAllUsersMetrics: GetAllUsersMetrics;
  getAllGroupsUser?: GetAllGroupsUserUseCaseInterface;
  getAllCoursesUser?: GetAllCoursesUserUseCaseInterface;
  getAllSemestersUser?: GetAllSemestersUserUseCaseInterface;
  getAllOrganizationsUser?: GetAllOrganizationsUserUseCaseInterface;
  getAllCurricularUnitsUser?: GetAllCurricularUnitsUserUseCaseInterface;
}
export const Dashboard = ({
  getAllGroupsUser,
  getAllCoursesUser,
  getAllSemestersUser,
  getAllOrganizationsUser,
  getAllCurricularUnitsUser,
}: IProps) => {
  // States
  const [isRight, setIsRight] = useState<boolean>(false);
  const [mapStatus, setMapStatus] = useState<string>('2');
  const [filtersDisabled, setFiltersDisabled] = useState<boolean>(true);

  const {
    groups: { groupsUser },
    courses: { coursesUser },
    semesters: { semestersUser },
    dashboard: {
      completedAndMissedActivitiesCount,
      activitiesInProgressCount,
      ch,
      users,
      activitiesByTask,
      activitiesByPlace,
      activitiesByGroup,
      activitiesByStudent,
      mapActivitiesMarkers,
      mapInternshipMarkers,
      mapActivityDetails,
    },
  } = useAppSelector((state) => ({
    groups: state.Groups,
    courses: state.Course,
    semesters: state.Semesters,
    dashboard: state.Dashboard,
  }));

  // Hooks
  const dispatch = useDispatch();
  const { currentUser } = useAuth();
  const { filters, saveFilters, filtersFormatted, clearFilters } = useFilters();

  const {
    requestsStatus,
    organizationsUser,
    curricularUnitsUser,
    loadingSummaryProfile,
    saveLastRegistrationApp,
    saveUsersOnlinePerPlatform,
    responseActivityListingInProgress,
    saveOnlineUsersDashboardIndicators,
    requestGetAllCHMetrics,
    requestGetAllGroupsUser,
    requestGetSummaryProfile,
    requestGetAllCoursesUser,
    requestGetAllUsersMetrics,
    requestGetAllSemestersUser,
    requestGetAllActivitiesByTask,
    requestGetAllActivitiesByGroup,
    requestGetCompletedAndMissedActivitiesCount,
    requestGetActivitiesInProgressCount,
    requestListLastRegistrationApp,
    requestGetAllOrganizationsUser,
    requestGetAllActivitiesByPlace,
    requestGetAllCurricularUnitsUser,
    requestGetActivitiesInProgressCountGroupedByOrganization,
    requestGetAllActivitiesByStudent,
    requestListUsersOnlinePerPlatform,
    requestListOnlineUsersPerOrganizationIndicators,
    requestGetMapActivitiesMarkers,
    requestGetMapActivityDetails,
    requestGetMapInternshipMarkers,
  } = useDashboard({
    getAllGroupsUser,
    getAllCoursesUser,
    getAllSemestersUser,
    getAllOrganizationsUser,
    getAllCurricularUnitsUser,
  });

  // Constants
  const formatDate = (date: Date) => date.toISOString().slice(0, 10);
  const navigate = useNavigate();

  const date = new Date();
  const currentDay = new Date();
  currentDay.setHours(0, 0, 0, 0);
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

  // Methods
  const toggleRightCanvas = () => {
    setIsRight(!isRight);
  };

  const updateMapStatus = (value: string) => {
    const filters = filtersFormatted();
    requestGetMapActivitiesMarkers({
      ...filters,
      status: value as '1' | '2' | '3',
    });
    requestGetMapInternshipMarkers({
      ...filters,
      status: value as '1' | '2' | '3',
    });
    setMapStatus(value);
  };

  const getAllDashboardData = useCallback(
    (filters: Partial<IFiltersFormatted>) => {
      Promise.all([
        requestGetAllCoursesUser(filters.entity),
        requestGetAllOrganizationsUser(),
        requestGetSummaryProfile(filters),

        // indicators calls
        requestGetAllUsersMetrics(filters),
        requestGetCompletedAndMissedActivitiesCount(filters),
        requestGetActivitiesInProgressCount(filters),
        requestGetAllCHMetrics(filters),

        // card indicators calls (tables)
        requestGetAllActivitiesByPlace({ length: 5, ...filters }),
        requestGetAllActivitiesByTask({ length: 5, ...filters }),
        requestGetAllActivitiesByStudent({ length: 5, ...filters }),
        requestGetAllActivitiesByGroup({ length: 5, ...filters }),

        // map call
      ]);
    },
    [],
  );

  const getOptionallyFiltersData = useCallback((filters: IFiltersFormatted) => {
    if (!filters.semester) requestGetAllSemestersUser(filters?.entity);

    if (!filters.curricularUnit)
      requestGetAllCurricularUnitsUser({
        entityId: filters?.entity,
        courseId: filters?.course,
      });

    if (!filters.group)
      requestGetAllGroupsUser({
        courseId: filters?.course,
        semesterId: filters?.semester,
        entityId: filters?.entity,
        curricularUnitId: filters?.curricularUnit,
      });
  }, []);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      entity: filters?.entity
        ? {
            label: filters?.entity?.label,
            value: filters?.entity?.value,
          }
        : null,
      course: filters?.course
        ? {
            label: filters?.course?.label,
            value: filters?.course?.value,
          }
        : null,
      semester: filters?.semester
        ? {
            label: filters?.semester?.label,
            value: filters?.semester?.value,
          }
        : null,
      unit: filters?.curricularUnit
        ? {
            label: filters?.curricularUnit?.label,
            value: filters?.curricularUnit?.value,
          }
        : null,
      group: filters?.group
        ? {
            label: filters?.group?.label,
            value: filters?.group?.value,
          }
        : null,
      period:
        filters?.period && filters?.starts && filters?.ends
          ? [new Date(filters.starts), new Date(filters.ends)]
          : [new Date(firstDayOfMonth), new Date(currentDay)],
    },
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      group: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      period: Yup.array().required('Período é obrigatório'),
    }),

    onSubmit: (values) => {
      dispatch(clearDashboardSummary());
      const period = values.period.map((date) =>
        date ? new Date(date) : null,
      );

      const valuesToSave = {
        entity: values.entity,
        course: values.course,
        semester: values.semester,
        curricularUnit: values.unit,
        group: values.group,
        period: values?.period,
        starts: period?.[0],
        ends: period?.[1],
      };

      saveFilters(valuesToSave as any);
      toggleRightCanvas();
    },
  });

  const handleChangeFilter = useCallback((values: IFiltersFormatted) => {
    if (values?.entity) {
      getOptionallyFiltersData(values);
      setFiltersDisabled(false);
    } else {
      setFiltersDisabled(true);
    }
  }, []);

  const requestFilterMaps = () => {
    const filters = filtersFormatted();
    requestGetMapActivitiesMarkers({
      ...filters,
      status: mapStatus as '1' | '2' | '3',
    });
    requestGetMapInternshipMarkers({
      ...filters,
      status: mapStatus as '1' | '2' | '3',
    });
  };

  useEffect(() => {
    const filtersFormatted2 = filtersFormatted();

    if (!filtersFormatted2) return;
    requestFilterMaps();

    getAllDashboardData(filtersFormatted2);
  }, [getAllDashboardData, filtersFormatted]);

  const { dashboardSummary } = useAppSelector(
    (state) => state.DashboardSummary,
  );

  // config chart donut
  const colorsCartDonut = ['#abb7b8', '#a3d9cd', '#1b6654'];

  const legendItems = [
    { label: 'Web', colorClass: colorsCartDonut[0], icon: 'globe' },
    {
      label: 'Profissional',
      colorClass: colorsCartDonut[1],
      icon: 'smartphone',
    },
    {
      label: 'Estudante',
      colorClass: colorsCartDonut[2],
      icon: 'smartphone',
    },
  ];

  const labelsCartDonut = [
    legendItems[0].label,
    legendItems[1].label,
    legendItems[2].label,
  ];

  const titleChartDonut = 'USUÁRIOS ON-LINE POR PLATAFORMA';

  useEffect(() => {
    requestListLastRegistrationApp();
    requestListOnlineUsersPerOrganizationIndicators();
    requestListUsersOnlinePerPlatform();
    requestGetActivitiesInProgressCountGroupedByOrganization();
  }, []);

  const dataDashboardSummary = [
    {
      icon: {
        iconName: 'ri-user-2-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Coordenadores',
      counter: new Intl.NumberFormat('pt-BR').format(
        dashboardSummary?.coordinators ?? 0,
      ),
    },

    {
      icon: {
        iconName: 'ri-user-star-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Professores',
      counter: new Intl.NumberFormat('pt-BR').format(
        dashboardSummary?.teachers ?? 0,
      ),
    },

    {
      icon: {
        iconName: 'ri-user-star-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Preceptores',
      counter: new Intl.NumberFormat('pt-BR').format(
        dashboardSummary?.preceptors ?? 0,
      ),
    },

    {
      icon: {
        iconName: 'ri-shield-user-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Assistentes/Auxiliares',
      counter: new Intl.NumberFormat('pt-BR').format(
        dashboardSummary?.assistants ?? 0,
      ),
    },

    {
      icon: {
        iconName: 'ri-account-box-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Estudantes',
      counter: new Intl.NumberFormat('pt-BR').format(
        dashboardSummary?.students ?? 0,
      ),
    },
  ];

  return (
    <PageWrapper title="Dashboard">
      <Row className="overflow-hidden">
        <Col xs={12} md={12} lg={12}>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <h4 className="mb-4">
              Visão Geral - <span className="fw-light">Dashboard</span>
            </h4>

            <button
              type="button"
              className="btn btn-soft-primary btn-icon waves-effect waves-light rounded-pill"
              onClick={toggleRightCanvas}
            >
              <i className="ri-filter-2-line fs-16"></i>
            </button>
          </div>

          <NavFilters
            filters={filters}
            lastDayOfMonth={formatDate(currentDay)}
            firstDayOfMonth={formatDate(firstDayOfMonth)}
          />

          <WidgetsCH statusCH={requestsStatus.statusCH} data={ch} />

          <WidgetsActivities
            users={users}
            completedAndMissedActivitiesCount={
              completedAndMissedActivitiesCount
            }
            activitiesInProgressCount={activitiesInProgressCount}
            statusCompletedAndMissedActivitiesCount={
              requestsStatus.statusCompletedAndMissedActivitiesCount
            }
            statusActivitiesInProgressCount={
              requestsStatus.statusActivitiesInProgressCount
            }
          />

          <MetricSummaryWidgets
            iconLib="remix"
            data={dataDashboardSummary}
            loading={loadingSummaryProfile}
          />

          <Row>
            <CardIndicators
              data={activitiesByPlace}
              status={requestsStatus.statusActivitiesByPlace}
              title="LOCAIS DE ESTÁGIO MAIS UTILIZADOS"
              noDataTitle="Nenhum local de estágio encontrado"
              noDataDescription="Não há locais de estágio disponíveis para exibir no momento."
            />
            <CardIndicators
              data={activitiesByTask}
              status={requestsStatus.statusActivitiesByTask}
              title="TAREFAS MAIS UTILIZADAS"
              noDataTitle="Nenhuma tarefa encontrada"
              noDataDescription="Não há tarefas disponíveis para exibir no momento."
            />

            <CardIndicators
              data={activitiesByStudent}
              status={requestsStatus.statusActivitiesByStudent}
              title={`ESTUDANTES MAIS EFETIVOS`}
              isEffectiveStudentTable
              enableStudentRedirect
              onStudentClick={(id) => {
                navigate(`/pessoas/${id}`);
              }}
              noDataTitle="Nenhum estudante encontrado"
              noDataDescription="Não há estudantes disponíveis para exibir no momento."
            />
            <CardIndicators
              data={activitiesByGroup}
              status={requestsStatus.statusActivitiesByGroup}
              title="GRUPOS MAIS ATIVOS"
              hasCodeColumn
              noDataTitle="Nenhum grupo encontrado"
              noDataDescription="Não há grupos disponíveis para exibir no momento."
            />
          </Row>
          <Row>
            <Col xs={12} md={4}>
              <OrganizationOnlineUser
                statusDashboardIndicators={
                  requestsStatus.statusDashboardIndicatorsUsersOnline
                }
                columns={makeColumnsOnlineUsersDashboardIndicators}
                data={saveOnlineUsersDashboardIndicators}
                title="USUÁRIOS ON-LINE POR ORGANIZAÇÃO"
                noDataTitle="Nenhum usuário on-line no momento"
                noDataDescription="Não há usuários on-line disponíveis para exibir no momento."
              />
            </Col>
            <Col xs={12} md={4}>
              <LastRegistrationApp
                title="ÚLTIMOS CADASTROS NO APP"
                loading={requestsStatus.statusLastRegistrationApp}
                data={saveLastRegistrationApp?.map((item) => ({
                  name: item.name,
                  photo: item.photo,
                  course: item.course,
                  profile: item.profile?.name,
                  institution: item.organization?.name,
                  registrationTime: item.registrationTime,
                }))}
                noDataTitle="Nenhum cadastro encontrado"
                noDataDescription="Não há cadastros disponíveis para exibir no momento."
              />
            </Col>

            <Col xs={12} md={4} lg={4} xl={4}>
              <ChartUsersOnlinePerPlatform
                legendItems={legendItems}
                colorsCartDonut={colorsCartDonut}
                labelsCartDonut={labelsCartDonut}
                titleChartDonut={titleChartDonut}
                saveUsersOnlinePerPlatform={saveUsersOnlinePerPlatform}
                loading={requestsStatus.statusListUsersOnlinePerPlatform}
              />
            </Col>
          </Row>

          <Row>
            <React.Fragment>
              <Col md={12} lg={12} xl={12}>
                <ChartOngoingActivityByInstitution
                  loading={
                    requestsStatus.statusGetActivitiesInProgressCountGroupedByOrganization
                  }
                  height={350}
                  data={responseActivityListingInProgress?.data}
                  title="ATIVIDADES EM ANDAMENTO POR ORGANIZAÇÃO"
                />
              </Col>
            </React.Fragment>
          </Row>

          <DashboardMaps
            updateMapStatus={updateMapStatus}
            mapActivityDetails={mapActivityDetails}
            mapActivitiesMarkers={mapActivitiesMarkers}
            mapInternshipMarkers={mapInternshipMarkers}
            requestGetMapActivityDetails={requestGetMapActivityDetails}
            loadingMapActivityDetails={requestsStatus.statusMapActivityDetails}
            loadingMapActivitiesMarkers={
              requestsStatus.statusMapActivitiesMarkers
            }
            loadingMapInternshipMarkers={
              requestsStatus.statusMapInternshipMarkers
            }
          />
        </Col>
      </Row>
      <OffcanvasFilters
        removeSessionAndClearFiltersSalved={clearFilters}
        useAppSelector={useAppSelector}
        isRight={isRight}
        validation={validation}
        groupsUser={groupsUser}
        coursesUser={coursesUser}
        toggle={toggleRightCanvas}
        semestersUser={semestersUser}
        organizationsRequestStatus={requestsStatus.organizationsUser}
        coursesRequestStatus={requestsStatus.coursesUser}
        curricularUnitsRequestStatus={requestsStatus.curricularUnitsUser}
        semestersRequestStatus={requestsStatus.semestersUser}
        groupsRequestStatus={requestsStatus.groupsUser}
        organizationsUser={organizationsUser}
        curricularUnitsUser={curricularUnitsUser}
        requestGetAllCoursesUser={requestGetAllCoursesUser}
        showSemesterFilter={true}
        showUnityFilter={true}
        filtersDisabled={filtersDisabled}
        handleChangeFilter={handleChangeFilter}
      />
    </PageWrapper>
  );
};
